import { closest } from '@jotforminc/utils';

import {
  addClass, removeClass, forEach, hasClass, isVisible, map
} from './utils';

export default class Dropdown {
  constructor(params) {
    this.layoutParams = params.layoutParams;
    this.cards = params.cards;
    this.validateCard = params.validateCard;
    this.isPreviewMode = params.isPreviewMode;

    this.bindDropdownEvents = this.bindDropdownEvents.bind(this);
    this.bindSearchInput = this.bindSearchInput.bind(this);
    this.bindClickOutside = this.bindClickOutside.bind(this);
    this.bindDropdownKeyEvents = this.bindDropdownKeyEvents.bind(this);
    this.setInitialValues = this.setInitialValues.bind(this);
    this.createClickEvent = this.createClickEvent.bind(this);
    this.bindSelectChangeEvent = this.bindSelectChangeEvent.bind(this);
    this.init = this.init.bind(this);
    this.init();
  }

  init() {
    const jfDropdown = document.querySelectorAll('.jfInput-dropdown');
    this.elementsArr = [];
    forEach(jfDropdown, dropdown => {
      const items = this.getItems(dropdown);
      const elements = this.getElements(dropdown);
      this.elementsArr.push(elements);
      this.getList(items, elements.popupListUl);
      this.bindDropdownEvents(elements, items);
      this.bindSearchInput(elements, items);
      this.updatePopupPosition(elements);
      this.movePopupListBelowButton(elements);
    });

    this.setInitialValues();
  }

  movePopupListBelowButton(elements) {
    if (hasClass(elements.dropdown, 'isDrawer')) {
      const parent = elements.popupContainer.parentNode;
      elements.popupContainer.parentNode.removeChild(elements.popupContainer);
      elements.popupContainer.style.position = 'fixed'; // eslint-disable-line
      elements.popupContainer.style.fontSize = '16px'; // eslint-disable-line
      parent.parentNode.insertBefore(elements.popupContainer, parent.nextSibling);
    }
  }

  updatePopupPosition(elements) {
    if (hasClass(elements.dropdown, 'isDrawer')) {
      const rect = elements.dropdownWrapper.getBoundingClientRect();
      const parent = closest(elements.dropdownWrapper, '.jfCard');
      const parentRect = parent ? parent.getBoundingClientRect() : null;
      const popup = elements.popupContainer;
      if (parentRect) {
        if (window.document.documentMode) { // IE only
          popup.style.top = `${rect.bottom}px`;
          popup.style.left = `${rect.left}px`;
        } else {
          popup.style.top = `${rect.bottom - parentRect.top}px`;
          popup.style.left = `${rect.left - parentRect.left}px`;
        }
      }
      popup.style.width = `${rect.width}px`;
    }
  }

  getElements(dropdown) {
    return {
      dropdown,
      popupListUl: dropdown.querySelector('.jfDropdown-optionList'),
      popupContainer: dropdown.querySelector('.jfDropdown-optionListWrapper'),
      select: dropdown.parentNode.querySelector('select'),
      searchInput: dropdown.querySelector('.jfDropdown-search'),
      defaultChips: dropdown.querySelectorAll('.defaultChip'),
      chipContainer: dropdown.querySelector('.jfDropdown-chipContainer'),
      dropdownWrapper: dropdown.querySelector('.jfDropdown-wrapper')
    };
  }

  getList(items, _ul) {
    const ul = _ul;

    while (ul.hasChildNodes()) { ul.removeChild(ul.firstChild); }

    forEach(items, item => {
      ul.appendChild(item);
    });
  }

  getItems(dropdown) {
    const list = dropdown.querySelector('.jfDropdown-optionList');
    const items = list.querySelectorAll('.jfDropdown-optionListItem');
    if (hasClass(dropdown, 'isShuffle')) {
      const nodes = Array.prototype.slice.call(items);
      let shuffled = nodes.slice(1);
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const temp = shuffled[i];
        shuffled[i] = shuffled[j];
        shuffled[j] = temp;
      }
      shuffled = [nodes[0], ...shuffled];
      return shuffled;
    }
    return items;
  }

  filterOptionList(val, items, elements) {
    const newItems = [];
    const selectedOptions = [];
    forEach(elements.select.querySelectorAll('option[selected="true"]'), selectedOption => {
      selectedOptions.push(selectedOption.getAttribute('value'));
    });
    const isMultiple = hasClass(elements.dropdown, 'isMultiple');
    forEach(items, item => {
      const itemValue = item.innerText || item.getAttribute('data-value');
      if ((!isMultiple || selectedOptions.indexOf(itemValue) < 0) && itemValue.toLowerCase().indexOf(val.toLowerCase()) > -1) {
        newItems.push(item);
      }
    });
    if (newItems.length === 0) {
      const noResult = items[0].cloneNode(false);
      removeClass(noResult, 'isSelected');
      noResult.removeAttribute('aria-selected');
      noResult.removeAttribute('data-value');
      noResult.innerText = `No Result Found For "${val}"`;
      newItems.push(noResult);
    }
    return newItems;
  }

  bindSearchInput(elements, items) {
    const { searchInput } = elements;
    const { dropdown } = elements;
    const placeholder = searchInput ? searchInput.placeholder || 'Search' : 'Search';
    const isMultiple = hasClass(dropdown, 'isMultiple');
    if (searchInput) {
      searchInput.addEventListener('keyup', e => {
        const searchVal = e.target.value;
        const newItems = this.filterOptionList(searchVal, items, elements);
        this.getList(newItems, elements.popupListUl);
      });
      if (!isMultiple) {
        searchInput.addEventListener('focus', () => {
          searchInput.value = '';
          const searchInputPlaceholder = searchInput.getAttribute('placeholder') || placeholder;
          searchInput.setAttribute('placeholder', searchInputPlaceholder);
          const newItems = this.filterOptionList(searchInput.value, items, elements);
          this.getList(newItems, elements.popupListUl);
          this.openDropdown(elements);
        });
        searchInput.addEventListener('blur', () => {
          this.setSearchInputValue(elements);
        });
      }
    }
  }

  bindDropdownEvents(elements, items) {
    const selectEl = elements.select;
    const { dropdown } = elements;
    const isMultiple = hasClass(dropdown, 'isMultiple');
    const { searchInput } = elements;

    forEach(items, item => {
      item.addEventListener('keypress', e => {
        if (e.keyCode === 13 || e.keyCode === 32) {
          e.preventDefault();
          e.stopPropagation();
          e.target.click();
        }
      });
    });
    // update on multiple default selections
    //
    const { defaultChips } = elements;
    if (defaultChips) {
      defaultChips.forEach(defaultChip => {
        defaultChip.querySelector('.removeChip').addEventListener('click', e => {
          const chipVal = defaultChip.getAttribute('data-value');
          e.stopPropagation();
          e.preventDefault();
          const newList = [];
          forEach(items, item => {
            if (document.body.contains(item) || item === chipVal) {
              newList.push(item);
            }
          });

          this.getList(newList, elements.popupListUl);
          defaultChip.remove();

          const jfOpts = elements.popupListUl.querySelectorAll('.jfDropdown-optionListItem');
          forEach(jfOpts, opt => {
            if (opt.getAttribute('data-value') === chipVal) {
              opt.removeAttribute('aria-selected');
              removeClass(opt, 'isSelected');
            }
          });

          const opts = elements.select.querySelectorAll(`option[value="${chipVal}"]`);
          forEach(opts, opt => {
            opt.removeAttribute('selected');
          });
          this.fireChangeEvent(selectEl);
        });
      });
    }

    const customList = elements.popupListUl;

    customList.addEventListener('click', event => {
      const option = event.target;
      const optVal = option.getAttribute('data-value');
      const optionValue = optVal === option.innerHTML ? optVal : option.innerHTML;
      elements.select.focus();
      if (optVal === null) return;

      if (!isMultiple) {
        forEach(items, item => {
          item.removeAttribute('aria-selected');
          removeClass(item, 'isSelected');
        });
        option.setAttribute('aria-selected', true);
        addClass(option, 'isSelected');
        const selectOptionValue = optVal.replace(/["\\]/g, '\\$&').trim();
        const selectOption = selectEl.querySelector(`option[value="${selectOptionValue}"]`);
        forEach(selectEl.querySelectorAll('option'), p => { p.removeAttribute('selected'); });
        if (selectOption) {
          selectOption.setAttribute('selected', true);
        }

        selectEl.value = optVal;
        this.closeDropdown(elements);

        const singleSelect = dropdown.querySelector('.isSingle');
        // added for translation value of dropdown option
        singleSelect.innerHTML = optionValue;

        if (searchInput) {
          searchInput.value = optionValue.replace('&amp;', '&');
        }

        if (
          hasClass(selectEl, 'form-subproduct-option')
          && typeof global.PaymentStock !== 'undefined'
        ) {
          global.PaymentStock.handleStockManagement();
        }

        this.fireChangeEvent(selectEl);
      } else {
        // replace for double quotes
        const replacedOptVal = optVal.replace(/["\\]/g, '\\$&');
        const options = elements.select.querySelectorAll(`option[value="${replacedOptVal}"]`);

        forEach(options, opt => {
          opt.setAttribute('selected', true);
        });

        const exists = map(elements.chipContainer.querySelectorAll('.jfDropdown-chip'), p => p.getAttribute('data-value')).indexOf(optVal) > -1;

        if (!exists) {
          const chip = document.createElement('span');
          // added for translation value of dropdown option
          chip.innerHTML = `${optionValue}<button class="removeChip" aria-label="Deselect ${optionValue}">&times;</button>`; // <i className="fa fa-times" aria-hidden="true"></i>
          chip.setAttribute('class', 'jfDropdown-chip');
          chip.setAttribute('data-value', optVal);

          elements.chipContainer.appendChild(chip);
          option.remove();
          const removeChipButton = chip.querySelector('.removeChip');
          removeChipButton.addEventListener('click', e => {
            e.stopPropagation();
            e.preventDefault();
            const newList = [];
            forEach(items, item => {
              if (document.body.contains(item) || item === option) {
                newList.push(item);
              }
            });

            this.getList(newList, elements.popupListUl);
            chip.remove();

            const opts = elements.select.querySelectorAll(`option[value="${replacedOptVal}"]`);
            forEach(opts, opt => {
              opt.removeAttribute('selected');
            });
            this.fireChangeEvent(selectEl);
          });
          removeChipButton.addEventListener('keypress', e => {
            if (e.keyCode === 13 || e.keyCode === 32) {
              e.preventDefault();
              e.stopPropagation();
              removeChipButton.click();
            }
          });
          this.fireChangeEvent(selectEl);
          searchInput.value = '';
        }
      }
    });

    elements.dropdownWrapper.addEventListener('click', e => {
      if (!elements.select.hasAttribute('disabled')) {
        e.preventDefault();
        elements.select.focus();
        if (this.isPopupOpen(elements) && searchInput) {
          searchInput.focus();
          return;
        }
        if (this.isPopupOpen(elements)) {
          this.closeDropdown(elements);
        } else {
          this.openDropdown(elements);
        }
      }
    });

    // Keyboard Events
    dropdown.addEventListener('keydown', event => this.bindDropdownKeyEvents(event, elements), true);

    // ClickOutside
    document.addEventListener('click', event => this.bindClickOutside(event, elements));

    // Blur
    dropdown.addEventListener('blur', () => this.closeDropdown(elements));

    // window resize
    window.addEventListener('resize', () => this.closeDropdown(elements));

    // find dropdown's scrollable parent and add event
    const scrollableDiv = closest(dropdown, '.jfProductModule-wrapper', 'jfCard-question') || closest(dropdown, '.jfQuestion-fields', 'jfCard-question');
    scrollableDiv.addEventListener('scroll', () => this.scrollParent(elements));

    // bind select element's change event
    elements.select.addEventListener('change', () => this.bindSelectChangeEvent(elements));
  }

  bindClickOutside(event, elements) {
    if (this.isPopupOpen(elements)) {
      const isMultiple = hasClass(elements.dropdown, 'isMultiple');
      const { searchInput } = elements;
      if (!elements.dropdownWrapper.contains(event.target) && !elements.popupContainer.contains(event.target)) {
        this.closeDropdown(elements);

        if (!isMultiple && searchInput) {
          this.setSearchInputValue(elements);
        }
      }
    }
  }

  bindDropdownKeyEvents(event, elements) {
    if (this.isDropdownDisabled(elements)) {
      return;
    }

    const { dropdown } = elements;
    const { keyCode } = event;
    switch (keyCode) {
      // ENTER
      case 13: {
        if (this.isPopupOpen(elements)) {
          event.preventDefault();
          event.stopPropagation();
          this.selectFromKeyboard(elements);
        }
        break;
      }
      // SPACE
      case 32: {
        if (!this.isPopupOpen(elements)) {
          if (closest(event.target, '.jfDropdown-chipContainer') !== null) { // allow removeChip button to be pressed by keyboard
            break;
          }
          event.preventDefault();
          event.stopPropagation();
          this.openDropdown(elements);
        } else {
          const searchInput = dropdown.querySelector('.jfDropdown-search');
          if (!searchInput) {
            event.preventDefault();
            event.stopPropagation();
            this.selectFromKeyboard(elements);
            this.closeDropdown(elements);
          }
        }
        break;
      }
      // UP ARROW
      case 38: {
        event.preventDefault();
        if (!this.isPopupOpen(elements)) {
          this.openDropdown(elements);
        } else {
          this.moveNextPrevElement(elements, 'previousElementSibling');
        }
        break;
      }
      // DOWN ARROW
      case 40: {
        event.preventDefault();
        if (!this.isPopupOpen(elements)) {
          this.openDropdown(elements);
        } else {
          this.moveNextPrevElement(elements, 'nextElementSibling');
        }
        break;
      }
      default: {
        break;
      }
    }
  }

  selectFromKeyboard(elements) {
    const jsSelected = elements.popupListUl.querySelector('.js-selected') ? elements.popupListUl.querySelector('.js-selected') : elements.popupListUl.querySelector('.isSelected');
    if (jsSelected && jsSelected.getAttribute('data-value')) {
      removeClass(jsSelected, 'js-selected');
      const { searchInput } = elements;
      if (searchInput) searchInput.blur();
      jsSelected.click();
      elements.dropdown.focus();
    }
  }

  closeDropdown(elements) {
    const { searchInput } = elements;

    if (searchInput) {
      this.toggleSearchInput(elements, false);
    }

    removeClass(elements.popupContainer, 'isOpen');
    elements.popupContainer.setAttribute('aria-hidden', true);
    elements.dropdownWrapper.setAttribute('aria-expanded', false);
    const jfOpts = elements.popupListUl.querySelectorAll('.jfDropdown-optionListItem');
    jfOpts.forEach(opt => opt.removeAttribute('tabindex'));
  }

  closeAllDropdowns() {
    this.elementsArr.forEach(p => this.closeDropdown(p));
  }

  isDropdownDisabled(elements) {
    return elements.select.hasAttribute('disabled');
  }

  isPopupOpen(elements) {
    return hasClass(elements.popupContainer, 'isOpen');
  }

  openDropdown(elements) {
    const { searchInput } = elements;
    addClass(elements.popupContainer, 'isOpen');
    elements.popupContainer.removeAttribute('aria-hidden');
    elements.dropdownWrapper.setAttribute('aria-expanded', true);
    const jfOpts = elements.popupListUl.querySelectorAll('.jfDropdown-optionListItem');
    jfOpts.forEach(opt => opt.setAttribute('tabindex', 0));
    if (searchInput) {
      this.toggleSearchInput(elements, true);
      searchInput.focus();
    }
    this.updatePopupPosition(elements);
  }

  toggleSearchInput(elements, show) {
    if (elements.dropdown.querySelector('.isSelected')) {
      const chipSpan = elements.chipContainer.querySelector('span');
      chipSpan.style.display = show ? 'none' : 'block';

      const { searchInput } = elements;
      searchInput.style.display = show ? 'block' : 'none';
    }
  }

  // direction can be nextElementSibling or previousElementSibling
  moveNextPrevElement(elements, direction) {
    let alignToTop;
    if (direction === 'nextElementSibling') {
      alignToTop = false;
    } else if (direction === 'previousElementSibling') {
      alignToTop = false;
    } else {
      return;
    }

    const jsSelected = elements.popupListUl.querySelector('.js-selected');
    if (jsSelected) {
      const nextElement = jsSelected[direction];
      if (nextElement) {
        removeClass(jsSelected, 'js-selected');
        addClass(nextElement, 'js-selected');
        nextElement.scrollIntoView(alignToTop);
      }
    } else {
      const isSelected = elements.popupListUl.querySelector('.isSelected');
      if (isSelected) {
        const nextElement = isSelected[direction];
        if (nextElement) {
          addClass(nextElement, 'js-selected');
          nextElement.scrollIntoView(alignToTop);
        }
      } else {
        const firstOption = elements.popupListUl.querySelector('.jfDropdown-optionListItem');
        if (firstOption) {
          addClass(firstOption, 'js-selected');
          firstOption.scrollIntoView(alignToTop);
        }
      }
    }
  }

  scrollParent(elements) {
    if (this.isPopupOpen(elements)) {
      if (isVisible(elements.dropdown)) {
        this.updatePopupPosition(elements);
      } else {
        this.closeDropdown(elements);
      }
    }
  }

  setSearchInputValue(elements) {
    const { select, select: { selectedIndex, value: selectValue }, searchInput } = elements;
    const selectText = select[selectedIndex].innerHTML;
    searchInput.value = selectText || selectValue || '';
  }

  fireChangeEvent(selectElement) {
    const changeEvent = document.createEvent('HTMLEvents');
    changeEvent.initEvent('change', true, true);
    selectElement.dispatchEvent(changeEvent);
  }

  setInitialValues() {
    this.elementsArr.forEach(elements => {
      const { dropdown } = elements;
      const isMultiple = hasClass(dropdown, 'isMultiple');

      if (isMultiple) {
        const selectedItems = map(elements.select.querySelectorAll('option'), p => p).filter(p => p.selected && p.innerText !== '');
        if (selectedItems.length) {
          const values = selectedItems.map(p => p.getAttribute('value'));
          values.forEach(value => {
            const element = map(elements.popupListUl.querySelectorAll('li'), p => p).find(p => p.getAttribute('data-value') === value);
            if (element) {
              this.createClickEvent(element);
            }
          });
        }
      } else if (elements.select.value) {
        const element = map(elements.popupListUl.querySelectorAll('li'), p => p).find((p, i) => p.getAttribute('data-value') === elements.select.value && i !== 0);
        if (element) {
          this.createClickEvent(element);
        }
      }
    });
  }

  createClickEvent(target) {
    if (!this.layoutParams.hasTouch || this.isPreviewMode) {
      const event = global.document.createEvent('MouseEvents');
      event.initMouseEvent('click', true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
      target.dispatchEvent(event);
    }
  }

  bindSelectChangeEvent(elements) {
    const { dropdown, select } = elements;

    if (hasClass(dropdown, 'form-validation-error')) {
      const component = elements.select.getAttribute('data-component');

      let qid;
      switch (component) {
        case 'dropdown':
          qid = elements.select.id;
          break;
        default:
          qid = `input_${elements.select.id.split('_')[1]}`;
          break;
      }

      const card = this.cards.filter(({ question }) => question.qid === qid)[0];

      if(card.question.type === 'control_mixed') {
        select.validateInput();
      } else {
        this.validateCard(card);
      }
    }

    const isMultiple = hasClass(dropdown, 'isMultiple');

    // We don't use custom dropdown on mobile devices. So don't need to manipulate click event.
    if (!this.layoutParams.hasTouch && !isMultiple) {
      const element = map(elements.popupListUl.querySelectorAll('li'), p => p).find(p => p.getAttribute('data-value') === elements.select.value);
      if (element && !hasClass(element, 'isSelected')) {
        this.createClickEvent(element);
      }
    }
  }
}
